import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Linkedin, Facebook } from "lucide-react";
import { useSite } from "@/hooks/useSite";
import { trackEvent } from "@/lib/analytics";
import featureFlags from "@/config/featureFlags.json";

export default function Footer() {
  const site = useSite();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#1A1A1A] text-[#F9F6F0] pt-20 pb-10 mt-24 relative" data-testid="site-footer">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        {/* Big "Let's talk" */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="border-b border-white/10 pb-16 mb-16"
        >
          <span className="eyebrow text-white/60">— {site?.pais || "Costa Rica"}</span>
          <h2 className="mt-6 font-serif text-[clamp(3rem,10vw,10rem)] leading-[0.9] tracking-[-0.02em]">
            Conversemos.
          </h2>
          <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <a
              href={`mailto:${site?.email || "contacto@kbconsultoria.com"}`}
              onClick={() => trackEvent("email_click", { source: "footer" })}
              data-testid="footer-email"
              className="font-serif text-2xl md:text-4xl italic link-underline"
            >
              {site?.email || "contacto@kbconsultoria.com"}
            </a>
            <a
              href="/#contacto"
              className="kb-btn bg-[#F9F6F0] text-[#1A1A1A] border-[#F9F6F0] hover:bg-[#1B3B2B] hover:text-[#F9F6F0] hover:border-[#1B3B2B]"
              data-testid="footer-cta"
            >
              Solicitar asesoría <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-sm text-white/70">
          <div>
            <img src="/kb-logo.png" alt="KB Consultoría" className="h-12 w-auto bg-[#F9F6F0]/95 p-1 rounded-sm mb-4" />
            <p className="text-xs leading-relaxed max-w-[240px]">
              Consultoría, implementación y auditoría en sistemas de gestión, ISO, sostenibilidad y seguridad de la información.
            </p>
          </div>

          <div>
            <div className="eyebrow text-white/50 mb-4">Navegación</div>
            <ul className="space-y-2">
              <li><Link to="/" className="link-underline">Inicio</Link></li>
              <li><a href="/#nosotros" className="link-underline">Nosotros</a></li>
              <li><a href="/#servicios" className="link-underline">Servicios</a></li>
              <li><Link to="/servicios/diagnostico-de-brechas" className="link-underline">Diagnóstico de brechas</Link></li>
              <li><Link to="/servicios/implementacion-de-sistemas-de-gestion" className="link-underline">Implementación</Link></li>
              <li><Link to="/servicios/auditorias-internas" className="link-underline">Auditorías internas</Link></li>
              <li><a href="/#metodologia" className="link-underline">Metodología</a></li>
              {featureFlags.showSuccessCases && <li><Link to="/casos-de-exito" className="link-underline">Casos de éxito</Link></li>}
              <li><Link to="/recursos" className="link-underline">Recursos</Link></li>
            </ul>
          </div>

          <div>
            <div className="eyebrow text-white/50 mb-4">Normas</div>
            <ul className="space-y-2">
              <li><Link to="/normas/iso-9001" className="link-underline">ISO 9001</Link></li>
              <li><Link to="/normas/iso-14001" className="link-underline">ISO 14001</Link></li>
              <li><Link to="/normas/iso-27001" className="link-underline">ISO/IEC 27001</Link></li>
              <li><Link to="/normas/fsc" className="link-underline">FSC</Link></li>
              <li><Link to="/normas/inte-g38" className="link-underline">INTE G38</Link></li>
              <li><Link to="/normas/bandera-azul-ecologica" className="link-underline">Bandera Azul</Link></li>
            </ul>
          </div>

          <div>
            <div className="eyebrow text-white/50 mb-4">Contacto</div>
            <ul className="space-y-2">
              <li>{site?.email}</li>
              <li>{site?.whatsapp_display}</li>
              <li>{site?.horario}</li>
              <li>{site?.direccion}</li>
            </ul>
            <div className="mt-6 flex gap-3">
              {site?.linkedin && <a href={site.linkedin} aria-label="LinkedIn" target="_blank" rel="noreferrer" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/20 hover:bg-white hover:text-[#1A1A1A] transition-colors"><Linkedin className="w-4 h-4" /></a>}
              {site?.instagram && <a href={site.instagram} aria-label="Instagram" target="_blank" rel="noreferrer" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/20 hover:bg-white hover:text-[#1A1A1A] transition-colors"><Instagram className="w-4 h-4" /></a>}
              {site?.facebook && <a href={site.facebook} aria-label="Facebook" target="_blank" rel="noreferrer" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/20 hover:bg-white hover:text-[#1A1A1A] transition-colors"><Facebook className="w-4 h-4" /></a>}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row md:justify-between gap-4 text-xs text-white/50">
          <div>© {year} KB Consultoría. Todos los derechos reservados.</div>
          <div className="flex gap-6">
            <Link to="/politica-privacidad" className="link-underline">Política de privacidad</Link>
            <Link to="/politica-cookies" className="link-underline">Cookies</Link>
            <Link to="/terminos" className="link-underline">Términos</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
